import React, { useState, useEffect, useRef } from 'react';
import { partnerAPI } from '../../utils/api';
import { useNotification } from '../../context/NotificationContext';
import { formatImageUrl } from '../../utils/imageUtils';

const PartnerManagement = () => {
  const { showSuccess, showError } = useNotification();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    type: 'organization',
    name: '',
    description: '',
    link_url: '',
    status: 'coming_soon',
    order: 0
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const res = await partnerAPI.getAll();
      if (res.data?.success) setPartners(res.data.data || []);
    } catch (error) {
      showError('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = (type) => {
    setForm({ type, name: '', description: '', link_url: '', status: 'coming_soon', order: 0 });
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (partner) => {
    setForm({
      type: partner.type,
      name: partner.name,
      description: partner.description || '',
      link_url: partner.link_url || '',
      status: partner.status,
      order: partner.order || 0
    });
    setEditing(partner);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showError('Name is required');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('type', form.type);
      fd.append('name', form.name);
      fd.append('description', form.description);
      fd.append('link_url', form.link_url);
      fd.append('status', form.status);
      fd.append('order', form.order);
      if (fileRef.current?.files[0]) fd.append('image', fileRef.current.files[0]);

      if (editing) {
        await partnerAPI.update(editing._id, fd);
        showSuccess('Partner updated');
      } else {
        await partnerAPI.create(fd);
        showSuccess('Partner created');
      }
      setShowForm(false);
      setEditing(null);
      fileRef.current = null;
      fetchPartners();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to save partner');
    }
  };

  const handleDelete = async (partner) => {
    if (!window.confirm(`Delete "${partner.name}"?`)) return;
    try {
      await partnerAPI.delete(partner._id);
      showSuccess('Partner deleted');
      fetchPartners();
    } catch (error) {
      showError('Failed to delete partner');
    }
  };

  const toggleStatus = async (partner) => {
    const nextStatus = partner.status === 'active' ? 'coming_soon' : 'active';
    try {
      const fd = new FormData();
      fd.append('status', nextStatus);
      await partnerAPI.update(partner._id, fd);
      showSuccess(`Partner ${nextStatus === 'active' ? 'enabled' : 'set to coming soon'}`);
      fetchPartners();
    } catch (error) {
      showError('Failed to update status');
    }
  };

  const orgPartners = partners.filter(p => p.type === 'organization');
  const wellnessPartners = partners.filter(p => p.type === 'wellness');

  if (loading) return <div className="p-6 text-center text-gray-400">Loading partners...</div>;

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold text-gold">Partner Management</h1>

      {/* Organization Partners */}
      <section>
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-semibold">Organization Partners</h2>
          <button onClick={() => openCreate('organization')} className="bg-gold text-dark px-3 py-1.5 rounded-lg text-sm font-medium">
            + Add
          </button>
        </div>
        <PartnerList
          partners={orgPartners}
          onEdit={openEdit}
          onDelete={handleDelete}
          onToggle={toggleStatus}
        />
      </section>

      {/* Wellness Partners */}
      <section>
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-semibold">Wellness Partners</h2>
          <button onClick={() => openCreate('wellness')} className="bg-gold text-dark px-3 py-1.5 rounded-lg text-sm font-medium">
            + Add
          </button>
        </div>
        <PartnerList
          partners={wellnessPartners}
          onEdit={openEdit}
          onDelete={handleDelete}
          onToggle={toggleStatus}
        />
      </section>

      {/* Modal Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gold mb-4">{editing ? 'Edit' : 'Add'} Partner</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-400 block mb-1">Type</label>
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="w-full bg-gray-700 rounded-lg p-2.5 text-white border border-gray-600">
                  <option value="organization">Organization</option>
                  <option value="wellness">Wellness</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full bg-gray-700 rounded-lg p-2.5 text-white border border-gray-600" placeholder="Partner name" />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full bg-gray-700 rounded-lg p-2.5 text-white border border-gray-600" rows={2} placeholder="Description" />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Link URL</label>
                <input value={form.link_url} onChange={e => setForm({ ...form, link_url: e.target.value })} className="w-full bg-gray-700 rounded-lg p-2.5 text-white border border-gray-600" placeholder="https://..." />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Banner Image</label>
                <input ref={fileRef} type="file" accept="image/*" className="w-full bg-gray-700 rounded-lg p-2 text-white border border-gray-600 file:mr-2 file:bg-gold file:text-dark file:border-0 file:rounded file:px-3 file:py-1 file:font-medium" />
                {editing?.image_url && (
                  <img src={formatImageUrl(editing.image_url)} alt="" className="mt-2 h-20 w-auto rounded object-cover" />
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Status</label>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full bg-gray-700 rounded-lg p-2.5 text-white border border-gray-600">
                    <option value="coming_soon">Coming Soon</option>
                    <option value="active">Active</option>
                    <option value="hidden">Hidden</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Order</label>
                  <input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="w-full bg-gray-700 rounded-lg p-2.5 text-white border border-gray-600" />
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowForm(false)} className="flex-1 bg-gray-700 text-white py-2.5 rounded-lg font-medium">Cancel</button>
              <button onClick={handleSave} className="flex-1 bg-gold text-dark py-2.5 rounded-lg font-medium">{editing ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PartnerList = ({ partners, onEdit, onDelete, onToggle }) => {
  if (partners.length === 0) {
    return (
      <div className="text-center py-6 text-gray-400 bg-gray-800/50 rounded-xl border border-dashed border-gray-700">
        <p>No partners added yet. Click "+ Add" to create one.</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {partners.map(partner => (
        <div key={partner._id} className="bg-gray-800 rounded-xl p-4 flex items-center gap-3">
          {partner.image_url ? (
            <img src={formatImageUrl(partner.image_url)} alt="" className="w-12 h-12 rounded-lg object-cover" />
          ) : (
            <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center text-gray-500 text-lg">
              {partner.type === 'organization' ? '🏢' : '🌿'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-medium truncate">{partner.name}</h3>
            <p className="text-xs text-gray-400 truncate">{partner.description || 'No description'}</p>
            <span className={`text-xs font-medium ${partner.status === 'active' ? 'text-green-400' : partner.status === 'coming_soon' ? 'text-yellow-400' : 'text-gray-500'}`}>
              {partner.status.replace(/_/g, ' ')}
            </span>
          </div>
          <div className="flex gap-1.5 shrink-0">
            <button onClick={() => onToggle(partner)} className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 text-sm" title={partner.status === 'active' ? 'Set Coming Soon' : 'Set Active'}>
              {partner.status === 'active' ? '⏸️' : '✅'}
            </button>
            <button onClick={() => onEdit(partner)} className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 text-sm" title="Edit">
              ✏️
            </button>
            <button onClick={() => onDelete(partner)} className="p-2 bg-red-900/30 rounded-lg hover:bg-red-800/40 text-sm" title="Delete">
              🗑️
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PartnerManagement;
